import type { PermissionsSignals } from '@fraudeai/schema';
import { defineModule } from '../harness.js';
import { attempt, attemptAsync } from '../util/safe.js';

/**
 * Состояния Permissions API.
 *
 * Читаются только состояния — ничего не запрашивается, диалогов пользователь
 * не увидит. Полезны двумя вещами: набор поддерживаемых имён различается между
 * браузерами (ещё одна сверка с UA), а состояния выдают headless-сборки, где
 * менеджер разрешений ведёт себя не так, как в настоящем браузере.
 */

const PERMISSION_NAMES = [
  'accelerometer',
  'ambient-light-sensor',
  'background-sync',
  'camera',
  'clipboard-read',
  'clipboard-write',
  'geolocation',
  'gyroscope',
  'local-fonts',
  'magnetometer',
  'microphone',
  'midi',
  'notifications',
  'payment-handler',
  'persistent-storage',
  'push',
  'screen-wake-lock',
  'storage-access',
  'window-management',
];

export const permissionsModule = defineModule({
  id: 'permissions',
  phase: 1,
  timeoutMs: 3000,
  async run(): Promise<PermissionsSignals> {
    if (!navigator.permissions?.query) {
      return { supported: false, states: null, notificationMismatch: null };
    }

    const entries = await Promise.all(
      PERMISSION_NAMES.map(async (name) => {
        const status = await attemptAsync(() =>
          navigator.permissions.query({ name: name as PermissionName }),
        );
        // Неподдерживаемое имя бросает TypeError — фиксируем это отдельно
        // от реального состояния разрешения.
        return [name, status ? status.state : 'unsupported'] as const;
      }),
    );

    const states = Object.fromEntries(entries);

    const notification = (globalThis as { Notification?: { permission: string } }).Notification;
    const notificationMismatch =
      notification && states['notifications'] && states['notifications'] !== 'unsupported'
        ? attempt(
            () => notification.permission === 'denied' && states['notifications'] === 'prompt',
            null,
          )
        : null;

    return { supported: true, states, notificationMismatch };
  },
});
